import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  ForecastReviewDataValidationProductSeriesResponse,
  ForecastReviewDataValidationSeriesPoint,
} from '@/services/forecastReviewService';
import { formatUiDate, formatUiNumber } from '@/utils/uiLocale';

type ChartPoint = ForecastReviewDataValidationSeriesPoint & {
  label: string;
};

interface DataValidationDemandChartProps {
  data: ForecastReviewDataValidationProductSeriesResponse | null;
  loading?: boolean;
}

function formatWeekLabel(period: string): string {
  if (period.includes('-W')) {
    const [, week] = period.split('-W');
    return `S${Number(week)}`;
  }
  return period;
}

function formatDateLabel(isoDate: string, language?: string | null): string {
  return formatUiDate(`${isoDate}T12:00:00`, { day: '2-digit', month: 'short', year: 'numeric' }, language);
}

function periodForDate(
  periodDate: string,
  periodByDate: Map<string, string>
): string | undefined {
  return periodByDate.get(periodDate);
}

function OutlierDot(props: {
  cx?: number;
  cy?: number;
  payload?: ChartPoint;
}) {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null || !payload?.is_outlier) return null;
  return <circle cx={cx} cy={cy} r={5} fill="#ea580c" stroke="#fff" strokeWidth={2} />;
}

function DefaultDot(props: { cx?: number; cy?: number; payload?: ChartPoint }) {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null || payload?.is_outlier) return null;
  return <circle cx={cx} cy={cy} r={2} fill="#1681ba" stroke="#1681ba" strokeWidth={1} />;
}

function ChartTooltip({
  active,
  payload,
  t,
  language,
}: {
  active?: boolean;
  payload?: Array<{ payload?: ChartPoint }>;
  t: (key: string, opts?: Record<string, unknown>) => string;
  language?: string | null;
}) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-md">
      <p className="font-medium text-gray-900">{formatDateLabel(point.period_date, language)}</p>
      <p className="mt-1 text-gray-700">
        {t('forecastModule.dataValidation.chart.sales')}:{' '}
        <span className="font-medium">{formatUiNumber(point.sales_quantity, undefined, language)}</span>
      </p>
      {point.is_outlier ? (
        <>
          <p className="mt-0.5 text-orange-700">
            {t('forecastModule.dataValidation.chart.observed')}:{' '}
            <span className="font-medium">
              {point.observed_quantity != null
                ? formatUiNumber(point.observed_quantity, undefined, language)
                : '—'}
            </span>
          </p>
          {point.reference_bound != null ? (
            <p className="mt-0.5 text-gray-600">
              {t('forecastModule.dataValidation.chart.referenceBound')}:{' '}
              <span className="font-medium">
                {formatUiNumber(point.reference_bound, undefined, language)}
              </span>
            </p>
          ) : null}
        </>
      ) : null}
      {point.is_gap ? (
        <p className="mt-0.5 text-gray-500">{t('forecastModule.dataValidation.chart.gapPeriod')}</p>
      ) : null}
      {point.is_stockout ? (
        <p className="mt-0.5 text-red-600">{t('forecastModule.dataValidation.chart.stockoutWeek')}</p>
      ) : null}
    </div>
  );
}

export function DataValidationDemandChart({ data, loading }: DataValidationDemandChartProps) {
  const { t, i18n } = useTranslation();

  const chartData = useMemo((): ChartPoint[] => {
    if (!data?.series.length) return [];
    return data.series.map((point) => ({
      ...point,
      label: formatWeekLabel(point.period),
    }));
  }, [data]);

  const periodByDate = useMemo(() => {
    const map = new Map<string, string>();
    for (const point of chartData) {
      map.set(point.period_date, point.period);
    }
    return map;
  }, [chartData]);

  const gapAreas = useMemo(() => {
    if (!data?.gap_periods.length) return [];
    return data.gap_periods
      .map((date) => periodForDate(date, periodByDate))
      .filter((period): period is string => Boolean(period));
  }, [data?.gap_periods, periodByDate]);

  const stockoutAreas = useMemo(() => {
    if (!data?.stockout_spans.length) return [];
    return data.stockout_spans
      .map((span) => ({
        x1: periodForDate(span.start_date, periodByDate),
        x2: periodForDate(span.end_date, periodByDate),
      }))
      .filter((area): area is { x1: string; x2: string } => Boolean(area.x1 && area.x2));
  }, [data?.stockout_spans, periodByDate]);

  const iqrReferenceLines = useMemo(() => {
    const bounds = new Set<number>();
    for (const point of chartData) {
      if (point.is_outlier && point.reference_bound != null) {
        bounds.add(point.reference_bound);
      }
    }
    return [...bounds].sort((a, b) => a - b);
  }, [chartData]);

  const chartMinWidth = Math.max(chartData.length * 28, 640);

  if (loading) {
    return (
      <div className="flex h-[320px] items-center justify-center text-sm text-gray-500">
        {t('forecastModule.dataValidation.chart.loading')}
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div className="flex h-[320px] flex-col items-center justify-center text-sm text-gray-500">
        <p>{t('forecastModule.dataValidation.chart.noData')}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3 text-[11px] text-gray-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-4 bg-[#1681ba]" aria-hidden />
          {t('forecastModule.dataValidation.chart.legendSales')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-orange-600" aria-hidden />
          {t('forecastModule.dataValidation.chart.legendOutlier')}
        </span>
        {iqrReferenceLines.length > 0 ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-0.5 w-4 border-t-2 border-dashed border-gray-500" aria-hidden />
            {t('forecastModule.dataValidation.chart.legendReferenceBound')}
          </span>
        ) : null}
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-4 rounded bg-gray-200" aria-hidden />
          {t('forecastModule.dataValidation.chart.legendGap')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-4 rounded bg-red-200" aria-hidden />
          {t('forecastModule.dataValidation.chart.legendStockout')}
        </span>
      </div>

      <div className="h-[320px] overflow-x-auto">
        <div style={{ minWidth: chartMinWidth, height: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.06)" />
              {gapAreas.map((period) => (
                <ReferenceArea
                  key={`gap-${period}`}
                  x1={period}
                  x2={period}
                  fill="#e5e7eb"
                  fillOpacity={0.55}
                  ifOverflow="extendDomain"
                />
              ))}
              {stockoutAreas.map((area) => (
                <ReferenceArea
                  key={`stockout-${area.x1}-${area.x2}`}
                  x1={area.x1}
                  x2={area.x2}
                  fill="#ef4444"
                  fillOpacity={0.18}
                  ifOverflow="extendDomain"
                />
              ))}
              {iqrReferenceLines.map((bound) => (
                <ReferenceLine
                  key={`iqr-${bound}`}
                  y={bound}
                  stroke="#6b7280"
                  strokeDasharray="6 4"
                  strokeWidth={1.5}
                  ifOverflow="extendDomain"
                />
              ))}
              <XAxis
                dataKey="period"
                tickFormatter={formatWeekLabel}
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
                minTickGap={16}
                angle={-45}
                textAnchor="end"
                height={56}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                width={48}
                tickFormatter={(v) =>
                  typeof v === 'number' ? formatUiNumber(v, undefined, i18n.language) : String(v)
                }
              />
              <Tooltip content={<ChartTooltip t={t} language={i18n.language} />} />
              <Line
                type="monotone"
                dataKey="sales_quantity"
                name={t('forecastModule.dataValidation.chart.sales')}
                stroke="#1681ba"
                strokeWidth={2}
                dot={(props) => {
                  if (props.payload?.is_outlier) return <OutlierDot {...props} />;
                  return <DefaultDot {...props} />;
                }}
                activeDot={{ r: 6, fill: '#1681ba' }}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
